/**
 * Acerta o botao de compra quando a pagina abre com uma medida esgotada na URL.
 *
 * O CASO
 *
 * Link de colecao, de filtro ou de busca carrega a variante junto do endereco:
 * `/products/x?variant=<id>`. Quando esse id e de uma medida esgotada, o Liquid
 * faz a coisa certa e desenha o botao como "Sold out", travado.
 *
 * So que o tema roda com `select_first_available_variant` ligado: assim que a
 * ficha do produto chega, o `<options-selection>` troca a selecao para a
 * primeira medida DISPONIVEL. A tela passa a mostrar uma medida a venda, e o
 * botao continua "Sold out" — travado numa pagina que poderia vender.
 *
 * Nao e intermitente. Com `?variant=` de medida esgotada, acontece sempre, no
 * tema publicado e no staging. Sem `?variant=` na URL, nao acontece nunca.
 *
 * POR QUE O TEMA NAO SE CORRIGE
 *
 * O `ProductDetails`, dentro do `empire.js`, so mexe no botao pelo
 * `_switchVariant`, que responde ao evento `variant-change`. Na inicializacao
 * ele acerta preco, selo e SKU — o botao, nao. E o ouvinte do `variant-change`
 * so e registrado depois que a ficha do produto chega da rede, enquanto a troca
 * automatica da selecao ja aconteceu antes disso. O aviso sai antes de existir
 * quem o ouca, e ninguem confere o botao depois.
 *
 * COMO CORRIGE, E POR QUE ASSIM
 *
 * Fica de fora do caminho do tema. NAO redispara `variant-change`: isso
 * acionaria o `_updateButton`, que substitui o conteudo inteiro do botao por
 * texto puro e apaga o `<span class="atc-button--icon">` — a rodinha de
 * carregando. Seria trocar um defeito por outro.
 *
 * A acao aqui e cirurgica: observa o atributo `variant` do `<variant-selection>`
 * e, quando ele muda, confere se o rotulo do botao bate com a disponibilidade da
 * variante ativa. So escreve quando diverge, e escreve dentro do
 * `<span class="atc-button--text">`, preservando a estrutura do botao.
 *
 * Os textos saem do `data-section-data` da propria pagina — os mesmos que o
 * tema usa. Nada fixo em ingles aqui dentro.
 *
 * Vale tambem para o quick shop: o modal entra na pagina depois, entao formulario
 * novo que aparece tambem passa a ser vigiado.
 */
(() => {
  const FORMULARIO = 'form[data-product-form]';
  const LIGADO = 'atcButtonSync';

  /**
   * Textos do botao, na lingua da loja, postos no layout pelo Liquid.
   *
   * Nao sai do `data-section-data` da secao de produto: no quick shop aquele
   * bloco nao sobrevive — o modal le o JSON e o descarta ao montar o conteudo,
   * e sem os textos esta correcao ficava muda justamente no quick shop.
   *
   * Sem o bloco, nao ha o que escrever no botao, e a funcao inteira se cala.
   * Falhar em silencio e melhor do que chutar palavra em ingles numa loja que
   * pode estar em outra lingua.
   */
  let textosEmCache;

  const lerTextos = () => {
    if (textosEmCache !== undefined) return textosEmCache;

    textosEmCache = null;

    const bloco = document.getElementById('atc-button-sync-textos');
    if (!bloco) return textosEmCache;

    try {
      const dados = JSON.parse(bloco.textContent);
      if (dados && dados.add_to_cart && dados.sold_out) {
        textosEmCache = dados;
      }
    } catch (erro) {
      // bloco malformado: segue sem textos, e a correcao nao age
    }

    return textosEmCache;
  };

  const conferir = (formulario) => {
    const selecao = formulario.querySelector('variant-selection');
    const botao = formulario.querySelector('[data-product-atc]');

    if (!selecao || !botao || typeof selecao.getProduct !== 'function') return;

    selecao.getProduct().then((produto) => {
      if (!produto || !produto.variants) return;

      const id = selecao.getAttribute('variant');
      const variante = produto.variants.find((v) => String(v.id) === String(id));

      // 'not-selected' e 'unavailable' nao sao variante: quem cuida deles e o
      // tema, e nesse ponto o ouvinte dele ja esta de pe.
      if (!variante) return;

      const textos = lerTextos();
      if (!textos) return;

      const preOrder = botao.hasAttribute('data-product-atc-preorder');
      const disponivel = variante.available;

      let alvo = textos.sold_out;
      if (disponivel) {
        alvo = preOrder ? textos.pre_order : textos.add_to_cart;
      }

      const rotulo = botao.querySelector('.atc-button--text') || botao;
      const bate = rotulo.textContent.trim() === alvo && botao.disabled === !disponivel;

      // silencio quando ja esta certo: no fluxo normal quem manda e o tema
      if (bate) return;

      rotulo.textContent = alvo;
      botao.disabled = !disponivel;
      botao.classList.toggle('disabled', !disponivel);
    });
  };

  const vigiar = (formulario) => {
    if (formulario.dataset[LIGADO]) return;

    const selecao = formulario.querySelector('variant-selection');
    if (!selecao) return;

    formulario.dataset[LIGADO] = '1';

    new MutationObserver(() => conferir(formulario)).observe(selecao, {
      attributes: true,
      attributeFilter: ['variant'],
    });

    conferir(formulario);
  };

  let agendado = false;

  const varrer = () => {
    agendado = false;
    document.querySelectorAll(FORMULARIO).forEach(vigiar);
  };

  // `setTimeout`, e nao `requestAnimationFrame`: em aba de fundo o navegador
  // nao entrega quadros, o `requestAnimationFrame` nunca chega, e a flag
  // ficaria presa em `true` — nenhuma varredura aconteceria mais naquela aba.
  // Abrir o produto em nova aba e coisa de todo dia.
  const agendarVarredura = () => {
    if (agendado) return;
    agendado = true;
    setTimeout(varrer, 0);
  };

  varrer();

  // quick shop e qualquer secao que chegue depois
  new MutationObserver(agendarVarredura).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // rede de seguranca: se o componente subiu depois do primeiro varrimento
  window.addEventListener('load', varrer);
})();
